import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { 
  ChartBarIcon, 
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';

function OrganizationsList() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchOrganizations() {
      try {
        const orgsRef = collection(db, 'organizations');
        const orgsSnapshot = await getDocs(orgsRef);
        
        // Get organizations with member counts (source of truth: members subcollection)
        const orgsData = await Promise.all(orgsSnapshot.docs.map(async (doc) => {
          const membersRef = collection(db, 'organizations', doc.id, 'members');
          const membersSnap = await getDocs(membersRef);
          const activeCount = membersSnap.docs.filter(d => d.data().status === 'active').length;
          return {
            id: doc.id,
            ...doc.data(),
            memberCount: activeCount
          };
        }));

        setOrganizations(orgsData);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
      setLoading(false);
    }

    fetchOrganizations();
  }, [isSuperAdmin]);

  if (loading) {
    return <div className="container mx-auto p-6">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header with navigation */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/app-admin')}
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Organizations List</h1>
        </div>
        <div className="flex space-x-4">
          {isSuperAdmin && (
            <>
              <Link
                to="/organization-metrics"
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                <ChartBarIcon className="h-5 w-5 mr-2" />
                View Metrics
              </Link>
              <Link
                to="/app-admin"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Back to Admin
              </Link>
            </>
          )}
        </div>
      </div>

      {organizations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organizations.map((org) => (
            <div 
              key={org.id} 
              className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">{org.name}</h3>
                  <p className="text-sm text-gray-500">{org.displayName}</p>
                  <p className="text-gray-600 text-sm mt-1">{org.bio}</p>
                  
                  <div className="mt-2 text-sm text-gray-500">
                    <span>{org.vitals?.country}</span>
                    {org.vitals?.website && (
                      <span className="ml-2">
                        <a 
                          href={org.vitals.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Website
                        </a>
                      </span>
                    )}
                  </div>
                </div>
                {org.type === 'active' ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <ClockIcon className="h-6 w-6 text-yellow-500" />
                )}
              </div>
              
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-600">Members: {org.memberCount || 0}</p>
                <p className="text-sm text-gray-600">
                  Created: {org.createdAt?.toDate()?.toLocaleDateString() || 'N/A'}
                </p>
              </div>

              <div className="flex justify-end space-x-2">
                <Link
                  to={`/organization/${org.id}`}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
                >
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">No organizations available.</p>
      )}
    </div>
  );
}

export default OrganizationsList;
